import { Browser } from '@capacitor/browser';
import type { PluginListenerHandle } from '@capacitor/core';
import { captureException } from '@sentry/nextjs';
import { useFormik } from 'formik';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo } from 'react';
import * as yup from 'yup';
import DesktopSignin from './DesktopSignin';
import useAuthStore, { useRedirectAfterSuccessAuth } from '../../hooks/auth';
import { isInMobile, isInDesktop } from '@fabric-app/compat';
import { resetStores } from '../../hooks/resetStores';
import { isObject, isString } from '@fabric-app/utils/guards';
import { isWoodyError } from '@fabric-app/business/errors/errors.utils';
import type { OAuthTypes } from '../../modules/auth/api/auth.api';
import { authApi } from '../../modules/auth/api/auth.api';
import {
  AuthPageWrapper,
  CenteringContainer,
} from '../../modules/auth/components/LayoutComponents';
import ErrorMessage from '../../components/ErrorMessage';
import { FooterTermsAndPolicy } from '../../modules/auth/components/FooterTermsAndPolicy';
import { pick } from '@fabric-app/utils/object/manipulation';
import { appRoutes } from '@fabric-app/business/app/routing/routes.config';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { logTrackerEvent, TrackerEventKind } from '@/src/lib/or';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { IconApple, IconGoogleLogo } from '@fabric-app/icons/old';
import { usePostSigninRedirect } from '@fabric-app/business/auth/hooks/usePostSigninRedirect';
import { useQueryOauthClient } from '@fabric-app/business/oauth/queries/useQueryOauthClient';
import { Flex } from '@fabric-app/ui/components/Flex';
import { IconOther48HomeIcon } from '@fabric-app/icons/svg';
import { P } from '@fabric-app/ui/components/Typography';
import { Image } from '@fabric-app/ui/components/Image';
import AvatarUser from '@fabric-app/business/user/components/AvatarUser/AvatarUser';
import { AuthForm } from '@/src/modules/auth/components/AuthForm';
import { TextInput } from '@fabric-app/ui/components/TextInput/TextInput';
import { motion } from 'framer-motion';
import { AuthPasswordTextInput } from '@/src/modules/auth/components/AuthPasswordInput';
import { useQueryClient } from '@tanstack/react-query';
import { createSubscriptionQueryKey } from '@fabric-app/business/pricing/queries/useQuerySubscription';
import { userQueryKeys } from '@fabric-app/business/user/queries/userQueryKeys';

const Signin: React.FC = () => {
  const router = useRouter();

  const { postSigninRedirect } = usePostSigninRedirect();
  const clientId =
    postSigninRedirect?.persistedQueryParams?.clientId &&
    isString(postSigninRedirect.persistedQueryParams.clientId)
      ? postSigninRedirect.persistedQueryParams.clientId
      : undefined;

  const { data: oauthClient } = useQueryOauthClient(clientId);

  const { login, isLoggedIn, authStatus } = useAuthStore((state) =>
    pick(state, ['login', 'isLoggedIn', 'authStatus']),
  );

  const redirectAfterSignin = useRedirectAfterSuccessAuth();

  const [loginError, setLoginError] = React.useState<string | null>(null);

  useEffect(() => {
    // if logged in, and on mobile, go to / and replace the history so that the user cannot go back to the login screen
    if (isLoggedIn && isInMobile()) {
      router.replace(appRoutes.home);
    }
  }, [isLoggedIn, router]);

  const isLoading = authStatus === 'loading';
  useRunOnce(resetStores, {
    condition: !isLoggedIn && !isLoading,
  });

  const [showDesktop, setShowDesktop] = React.useState(false);

  useEffect(() => {
    if (!isInDesktop()) return;

    setShowDesktop(true);
  }, [router]);

  const queryClient = useQueryClient();

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: yup.object({
      email: yup.string().email().required('Please enter your email'),
      password: yup.string().required('Please enter your password'),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async ({ email, password }, formik) => {
      formik.setSubmitting(true);
      try {
        const user = await login(email, password);
        queryClient.invalidateQueries({ queryKey: createSubscriptionQueryKey(user.id) });
        queryClient.invalidateQueries({ queryKey: userQueryKeys.userInfo() });
        redirectAfterSignin(user, {
          action: 'login',
        });
      } catch (e: unknown) {
        if (typeof e !== 'object' || e === null || !isObject(e) || typeof e.status !== 'number') {
          captureException(new Error('Failed to log in, unknown error'), {
            extra: {
              error: e,
            },
          });

          setLoginError('Something went wrong. Please try again later.');
          return;
        }

        if (
          e.status === 400 &&
          isObject(e) &&
          isWoodyError(e.message) &&
          (e.message.detail as string)?.includes('not confirmed')
        ) {
          await router.push({
            pathname: '/confirm',
            query: { email },
          });
        }
        if (e.status === 500) setLoginError('Something went wrong. Please try again later.');
        else setLoginError('Incorrect username or password.');
      } finally {
        formik.setSubmitting(false);
      }
    },
  });

  const touchedErrors = useMemo(() => {
    // Only return the errors where the touched field is true
    return Object.entries(formik.errors).reduce(
      (acc, [key, value]) => {
        if (formik.touched[key as keyof typeof formik.touched]) {
          acc[key as keyof typeof formik.errors] = value;
        }
        return acc;
      },
      {} as typeof formik.errors,
    );
  }, [formik]);

  const handleOauth = useCallback(
    async (provider: OAuthTypes) => {
      formik.setSubmitting(true);
      try {
        const { url } = await authApi.getOauthUrl({
          provider,
          asDeepLink: isInMobile(),
        });

        if (isInMobile()) {
          let listener: PluginListenerHandle | undefined = undefined;
          const onBrowserClosed = () => {
            logTrackerEvent({
              kind: TrackerEventKind.OAUTH_BROWSER_CLOSED,
              payload: {
                provider,
                url,
              },
            });

            listener?.remove();
          };
          listener = await Browser.addListener('browserFinished', onBrowserClosed);

          logTrackerEvent({
            kind: TrackerEventKind.OAUTH_BROWSER_OPEN,
            payload: {
              provider,
              url,
            },
          });

          await Browser.open({
            url,
            presentationStyle: 'popover',
          });
          return;
        }

        await router.replace(url);
      } catch (e: unknown) {
        console.error(e);
        formik.setFieldError(
          'email',
          e instanceof Error && isWoodyError(e.message) && process.env.NODE_ENV === 'development'
            ? e.message.detail
            : 'Something went wrong. Please try again.',
        );
      } finally {
        formik.setSubmitting(false);
      }
    },
    [formik, router],
  );

  return showDesktop ? (
    <DesktopSignin />
  ) : (
    <>
      <ScrollArea.Default>
        <AuthPageWrapper>
          <CenteringContainer>
            <AuthForm.Form onSubmit={formik.handleSubmit} as={motion.form}>
              {clientId && oauthClient ? (
                <Flex
                  gap="sectionsShort"
                  direction="column"
                  alignItems="center"
                  style={{
                    marginBottom: 28,
                  }}
                >
                  <IconOther48HomeIcon />
                  <Flex gap="element" direction="column" alignItems="center">
                    <P size="lg" weight={500}>
                      Sign in to Fabric
                    </P>
                    <Flex gap="element" alignItems="center">
                      <P size="lg" weight={500}>
                        to continue to
                      </P>
                      <Image
                        src={oauthClient.logoUrl ?? undefined}
                        alt={oauthClient.name}
                        width={24}
                        height={24}
                      >
                        <AvatarUser
                          user={{
                            id: oauthClient.id,
                            name: oauthClient.name,
                          }}
                          size={24}
                        />
                      </Image>
                      <P
                        size="lg"
                        weight={500}
                        style={{
                          textDecoration: 'underline',
                        }}
                      >
                        {oauthClient.name}
                      </P>
                    </Flex>
                  </Flex>
                </Flex>
              ) : (
                <AuthForm.Title>Sign in</AuthForm.Title>
              )}
              <Flex direction="column" gap={12}>
                <AuthForm.OauthButton
                  type="button"
                  onClick={() => handleOauth('apple')}
                  disabled={formik.isSubmitting}
                >
                  <IconApple />
                  <span className="min-w-[8.5rem]">Sign in with Apple</span>
                </AuthForm.OauthButton>
                <AuthForm.OauthButton
                  type="button"
                  onClick={() => handleOauth('google')}
                  disabled={formik.isSubmitting}
                >
                  <IconGoogleLogo />
                  <span className="min-w-[8.5rem]">Sign in with Google</span>
                </AuthForm.OauthButton>
                <AuthForm.Text>or</AuthForm.Text>
                <Flex direction="column" gap={10}>
                  <TextInput
                    variant="auth"
                    name="email"
                    type="email"
                    placeholder="Email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    disabled={formik.isSubmitting}
                    onBlur={formik.handleBlur}
                  />
                  <AuthPasswordTextInput
                    autoComplete="current-password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    disabled={formik.isSubmitting}
                    onBlur={formik.handleBlur}
                  />

                  {(Object.values(touchedErrors).length > 0 || loginError) && (
                    <ErrorMessage>{Object.values(touchedErrors)[0] ?? loginError}</ErrorMessage>
                  )}
                  <AuthForm.Button type="submit" isLoading={formik.isSubmitting} as={motion.button}>
                    Sign in
                  </AuthForm.Button>
                </Flex>
              </Flex>
            </AuthForm.Form>
            <AuthForm.Footer as={motion.div}>
              <p>
                <Link href={'/reset-password'}>Forgot password?</Link>
              </p>
              <p>
                No account?{' '}
                <Link prefetch href={appRoutes.auth.signup}>
                  Sign up
                </Link>
              </p>
            </AuthForm.Footer>
          </CenteringContainer>
          <FooterTermsAndPolicy showHomeLink />
        </AuthPageWrapper>
      </ScrollArea.Default>
    </>
  );
};

export default Signin;
